import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const OUTCOMES = [
  'in dir selbst absoluten Frieden finden.',
  'dich frei, sicher und tief verbunden fühlen.',
  'Liebe und Erfolg auf natürliche Weise anziehen.',
  'dein Leben mit klarem Bewusstsein und Leichtigkeit gestalten.',
];

const FuturePacing = () => (
  <section className="relative py-32 overflow-hidden bg-dark border-t border-gold/20">
    {/* Starfield / liquid gold background */}
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-dark" />
      <motion.div
        animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.1, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.08) 0%, transparent 60%)' }}
      />
      <motion.div
        animate={{ opacity: [0.2, 0.5, 0.2] }}
        transition={{ duration: 14, repeat: Infinity }}
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 20% 80%, rgba(114,47,55,0.1) 0%, transparent 50%)' }}
      />
      {/* Stars */}
      {[...Array(20)].map((_, i) => (
        <motion.div key={i}
          className="absolute rounded-full bg-cream"
          style={{
            width: Math.random() * 2 + 0.5,
            height: Math.random() * 2 + 0.5,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            opacity: 0,
          }}
          animate={{ opacity: [0, Math.random() * 0.5 + 0.1, 0] }}
          transition={{ duration: 3 + Math.random() * 5, repeat: Infinity, delay: Math.random() * 6 }}
        />
      ))}
    </div>

    {/* Pulsing frame */}
    <motion.div
      animate={{ opacity: [0.2, 0.5, 0.2] }}
      transition={{ duration: 5, repeat: Infinity }}
      className="absolute inset-8 pointer-events-none"
      style={{ border: '1px solid rgba(212,175,55,0.15)', borderRadius: '2px', boxShadow: '0 0 80px rgba(212,175,55,0.06), inset 0 0 80px rgba(212,175,55,0.03)' }}
    />

    <div className="relative z-10 max-w-5xl mx-auto px-6">
      <div className="text-center mb-20">
        <motion.p initial={{ opacity: 0, letterSpacing: '0.1em' }}
          whileInView={{ opacity: 1, letterSpacing: '0.4em' }}
          viewport={{ once: true }} transition={{ duration: 1.5 }}
          className="text-gold uppercase text-xs font-semibold mb-8"
          style={{ textShadow: '0 0 20px rgba(212,175,55,0.6)' }}>
          Future Pacing · Deine neue Realität
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.9 }}
          className="text-4xl md:text-6xl font-serif text-cream leading-tight mb-6">
          Nach dieser Reise wirst du...
        </motion.h2>
        <motion.div initial={{ opacity: 0, scaleX: 0 }} whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }} transition={{ duration: 1.5 }}
          className="w-32 h-[1px] mx-auto"
          style={{ background: 'linear-gradient(to right, transparent, #D4AF37, transparent)' }} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
        {OUTCOMES.map((outcome, i) => (
          <motion.div key={i}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.7 }}
            className="flex items-center gap-5 p-6 group cursor-default"
            style={{ background: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.1)', borderRadius: '2px' }}
            whileHover={{ borderColor: 'rgba(212,175,55,0.35)', background: 'rgba(212,175,55,0.06)' }}>
            <motion.span
              animate={{ opacity: [0.5, 1, 0.5], textShadow: ['0 0 5px rgba(212,175,55,0.3)', '0 0 20px rgba(212,175,55,0.9)', '0 0 5px rgba(212,175,55,0.3)'] }}
              transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
              className="text-gold text-xl shrink-0">✦</motion.span>
            <p className="text-cream/75 font-light leading-relaxed text-base group-hover:text-cream transition-colors duration-500">{outcome}</p>
          </motion.div>
        ))}
      </div>

      {/* Queen Circle */}
      <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 1 }}
        className="relative p-10 md:p-16 text-center overflow-hidden"
        style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '2px', boxShadow: '0 0 80px rgba(212,175,55,0.07)' }}>
        <div className="absolute top-0 left-0 right-0 h-[1px]"
          style={{ background: 'linear-gradient(to right, transparent, #D4AF37, transparent)' }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(212,175,55,0.06)_0%,transparent_60%)]" />

        <motion.div
          animate={{ opacity: [0.4, 1, 0.4], scale: [0.98, 1.02, 0.98] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="relative z-10">
          <p className="text-gold tracking-[0.35em] uppercase text-xs mb-6 font-semibold">Exklusive Community</p>
          <h3 className="text-4xl md:text-5xl font-serif text-cream mb-8 leading-tight">
            Die{' '}
            <span className="italic text-transparent bg-clip-text bg-gold-gradient">Queen Circle</span>{' '}
            Community
          </h3>
        </motion.div>

        <div className="relative z-10 space-y-5 text-cream/65 font-light text-lg leading-relaxed mb-12 max-w-2xl mx-auto">
          <p>
            Der Queen Circle ist dein sicherer Raum, in dem du dich selbst wiederfindest,
            deine Muster endgültig durchbrichst und in deine wahre Weiblichkeit zurückkehrst.
          </p>
          <p>
            Hier gehst du nicht mehr allein durch deine dunkelsten Themen, sondern{' '}
            <span className="text-cream font-serif italic">gemeinsam mit Frauen</span>, die genau
            verstehen, wo du stehst.
          </p>
          <p className="text-cream font-serif italic text-xl">
            Wenn du bereit bist, dich selbst zu wählen und dein Leben wirklich zu verändern,
            dann ist das dein nächster Schritt.
          </p>
        </div>

        <Link to="/programme">
          <motion.div
            whileHover={{ scale: 1.06, boxShadow: '0 0 70px rgba(212,175,55,0.8), 0 0 140px rgba(212,175,55,0.4)' }}
            whileTap={{ scale: 0.97 }}
            className="inline-block px-14 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm cursor-pointer relative overflow-hidden group"
            style={{ boxShadow: '0 0 40px rgba(212,175,55,0.5)', borderRadius: '2px' }}>
            <span className="relative z-10">✦ Tritt der Queen Circle Community bei</span>
            <div className="absolute inset-0 bg-white/15 translate-x-[-110%] skew-x-[-20deg] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
          </motion.div>
        </Link>
      </motion.div>
    </div>
  </section>
);

export default FuturePacing;